import React, { memo, useEffect } from 'react';

import { Box, Divider, Stack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { customTheme } from 'theme';

import { Card } from 'components/DataDisplay';
import { usePageContentContext } from 'components/Layout';
import { MetadataHeader } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataHeader';
import { ResourceMetadata } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useSearchParams } from 'hooks/useSearchParams';

export const ResourceMetadataPage = memo(() => {
  const params = useParams<{ id: string; resource_type: string }>();
  const searchParams = useSearchParams<{ metadata_tab: string }>();
  const dispatch = useDispatch();
  const { linkedResourceDetails } = useSelector(selectActiveResource);
  const { contentHeight } = usePageContentContext();
  const { getTabIndex } = useMetadataDrawerTabs();

  useEffect(() => {
    if (!params.id) return;
    dispatch(
      actions.getLinkedResourceDetails({
        q: {
          insightV2Request: {
            resource_uuids: [params.id!],
          },
        },
        onSuccess: res => {
          if (isEmpty(res?.data?.items)) return;
          const item = res?.data?.items?.[0];
          dispatch(
            actions.updateResource({
              resourceId: item?.resource_id,
              resourceType: item?.native_name,
              uuid: item?.uuid,
              accountId: item?.account,
              resourceArn: item?.arn ?? item?.resource_arn,
            }),
          );
        },
      }),
    );
  }, [dispatch, params.id]);

  useEffect(() => {
    if (!searchParams.metadata_tab) return;
    const index = getTabIndex(params.resource_type, searchParams.metadata_tab);
    if (index === -1) return;
    dispatch(actions.setActiveTabName(searchParams.metadata_tab));
    dispatch(actions.setActiveTabIndex(index));
  }, [dispatch, searchParams.metadata_tab, params.resource_type, getTabIndex]);

  return (
    <Card styles={{ card: { h: contentHeight + 'px' } }}>
      <Stack spacing={5} w="full" h="full">
        <MetadataHeader isLoading={linkedResourceDetails.isLoading} />
        <Divider color={customTheme.colors.gray['100']} />
        <Box flex={1} overflowY="auto">
          <ResourceMetadata />
        </Box>
      </Stack>
    </Card>
  );
});
